import type { LatLngLiteral } from 'leaflet';
import {
  Boundary,
  MeasurementSystem,
  MultiPolygonBoundary,
  MultiPolygonShape,
  PolyggonBoundary,
  PolygonShape,
  Shape,
} from './types';
import * as turf from '@turf/turf';

import { format, formatDistanceToNow } from 'date-fns';
import { ar, de, enUS, es, fr, pt } from 'date-fns/locale';
import { LangCode } from '@/i18n/actions';
import { Lang } from './hooks/useChangeLang';
import type { TierName } from './components/TierNames';
import { isEnterprisePlan } from './plan';

export const m2ToAcres = (m2: number) => m2 / 4046.8564224;

// add next to your existing enum (you already have MeasurementSystem defined)
export const m2ToHectares = (m2: number) => m2 / 10_000;

/** convert m² -> acres (imperial) or hectares (metric) */
export function convertAreaBySystem(
  m2: number,
  system: MeasurementSystem
): number {
  return system === MeasurementSystem.Imperial
    ? m2ToAcres(m2)
    : m2ToHectares(m2);
}

export function polygonArea(
  ring: LatLngLiteral[],
  system = MeasurementSystem.Metric
): number {
  if (!ring || ring.length < 3) return 0;

  // Convert to Turf format: [lng, lat] arrays, close ring
  const coords: [number, number][] = ring.map((p) => [p.lng, p.lat]);
  if (
    coords[0][0] !== coords[coords.length - 1][0] ||
    coords[0][1] !== coords[coords.length - 1][1]
  ) {
    coords.push(coords[0]); // Ensure closed
  }

  const polygon = turf.polygon([coords]);
  const meters2 = turf.area(polygon); // Returns m² accurately
  return convertAreaBySystem(meters2, system);
}
export function isPolygonShape(s: Shape): s is PolygonShape {
  return s?.type === 'Polygon';
}
export function isMultiPolygonShape(s: Shape): s is MultiPolygonShape {
  return s?.type === 'MultiPolygon';
}
/** True for both single- and multi-polygon plots. */
export function isPolygonLikeShape(
  s: Shape
): s is PolygonShape | MultiPolygonShape {
  return isPolygonShape(s) || isMultiPolygonShape(s);
}

/**
 * Every polygon of a shape as `[outerRing, ...holes]`.
 * A `Polygon` yields one entry, a `MultiPolygon` one per part.
 */
export function shapePolygons(s: Shape): LatLngLiteral[][][] {
  if (isMultiPolygonShape(s)) {
    return (s.coordinates ?? []).filter((poly) => poly?.[0]?.length >= 3);
  }
  if (isPolygonShape(s)) {
    if (!(s.coordinates?.length >= 3)) return [];
    return [[s.coordinates, ...(s.holes ?? [])]];
  }
  return [];
}

/** Outer ring of every polygon — what previews and hit-testing need. */
export function shapeOuterRings(s: Shape): LatLngLiteral[][] {
  return shapePolygons(s).map((poly) => poly[0]);
}

/**
 * Rings of exactly 2 points. A boundary like that encloses no area and draws as
 * a line, so it must be rendered as a polyline rather than a polygon.
 */
export function shapeLineRings(s: Shape): LatLngLiteral[][] {
  const rings = isMultiPolygonShape(s)
    ? (s.coordinates ?? []).map((poly) => poly?.[0])
    : isPolygonShape(s)
    ? [s.coordinates]
    : [];
  return rings.filter((r) => r?.length === 2);
}

/**
 * Every ring worth putting on the map: closed rings plus 2-point lines.
 * Use for bounds/zoom so a line-shaped plot still fits the viewport.
 */
export function shapeBoundsRings(s: Shape): LatLngLiteral[][] {
  return [...shapeOuterRings(s), ...shapeLineRings(s)];
}

/** Area of a polygon or multi-polygon plot: all parts summed, holes subtracted. */
export function polygonShapeArea(
  s: Shape,
  system = MeasurementSystem.Metric
): number {
  return shapePolygons(s).reduce((sum, [outer, ...holes]) => {
    const inner = holes.reduce((h, ring) => h + polygonArea(ring, system), 0);
    return sum + Math.max(polygonArea(outer, system) - inner, 0);
  }, 0);
}

/** Circle area (radius meters) -> acres */
export function circleAreaBySystem(
  radiusMeters: number,
  system = MeasurementSystem.Metric
): number {
  const meters2 = Math.PI * radiusMeters * radiusMeters;
  return convertAreaBySystem(meters2, system);
}

export function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const isClient = typeof window !== 'undefined';
    if (!isClient) {
      reject();
      return;
    }
    // Check if the script is already loaded
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve(); // Already present
      return;
    }

    // Create a new <script> element
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

    document.head.appendChild(script);
  });
}

export function boundaryToShape(boundary: Boundary): Shape {
  if (boundary.type === 'Point') {
    const [lat, lng] = boundary.coordinates;
    const radius = boundary.properties?.radius;
    if (radius == null || Number.isNaN(Number(radius))) {
      throw new Error('Point boundary requires properties.radius (meters).');
    }
    return {
      type: 'Circle',
      center: { lat: Number(lat), lng: Number(lng) },
      radius: Number(radius),
    };
  }

  const toRing = (ring: [number, number][]): LatLngLiteral[] =>
    ring.map(([lat, lng]) => ({ lat: Number(lat), lng: Number(lng) }));

  if (boundary.type === 'MultiPolygon') {
    const polygons = (boundary.coordinates ?? [])
      .filter((poly) => poly?.[0]?.length)
      .map((poly) => poly.map(toRing));
    if (polygons.length === 0) {
      throw new Error('MultiPolygon boundary must include at least one ring.');
    }
    // A single part is a plain polygon as far as the rest of the app is concerned.
    if (polygons.length === 1) {
      const [outer, ...holes] = polygons[0];
      return {
        type: 'Polygon',
        coordinates: outer,
        ...(holes.length > 0 ? { holes } : {}),
      };
    }
    return { type: 'MultiPolygon', coordinates: polygons };
  }

  // Polygon: outer ring first, remaining rings are holes
  const rings = boundary.coordinates ?? [];
  const outer = rings[0];
  if (!outer || outer.length === 0) {
    throw new Error(
      'Polygon boundary must include at least one ring with coordinates.'
    );
  }
  const holes = rings.slice(1).filter((r) => r?.length).map(toRing);
  return {
    type: 'Polygon',
    coordinates: toRing(outer),
    ...(holes.length > 0 ? { holes } : {}),
  };
}

function round(n: number, p?: number) {
  return p == null ? Number(n) : Number(n.toFixed(p));
}

function equalsLngLat(a: [number, number], b: [number, number]) {
  return a[0] === b[0] && a[1] === b[1];
}

export function shapeToBoundary(
  shape: Shape,
  opts?: { precision?: number }
): Boundary {
  const { precision } = opts ?? {};

  if (shape.type === 'Circle') {
    const lng = round(shape.center.lng, precision);
    const lat = round(shape.center.lat, precision);
    const radius = Number(shape.radius);
    return {
      type: 'Point',
      coordinates: [lat, lng],
      properties: {
        radius,
        geometryType: 'Circle',
      },
    };
  }

  // Polygon / MultiPolygon. Coordinates are [lat, lng] — Leaflet order, not RFC
  // 7946 — and rings go without their closing vertex: the API closes them on
  // store and strips it again on read, so a repeated first point is a duplicate.
  const toRing = (ring: LatLngLiteral[]): [number, number][] => {
    const out: [number, number][] = ring.map((p) => [
      round(p.lat, precision),
      round(p.lng, precision),
    ]);
    // A file can close a ring more than once; strip every repeat of the first point.
    while (out.length >= 4 && equalsLngLat(out[0], out[out.length - 1])) out.pop();
    return out;
  };

  const polygons = shapePolygons(shape).map((poly) => poly.map(toRing));

  if (polygons.length > 1) {
    const boundary: MultiPolygonBoundary = {
      type: 'MultiPolygon',
      coordinates: polygons,
    };
    return boundary;
  }

  const boundary: PolyggonBoundary = {
    type: 'Polygon',
    coordinates: polygons[0] ?? [],
  };

  return boundary;
}

export function toDateOnly(dateString: string): string {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
export function snakeToCamel(str: string): string {
  return str
    .toLowerCase()
    .replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
}

export function radiusFtToM(radiusFt: number): number {
  const feetToMeters = 0.3048; // 1 foot = 0.3048 meters
  return radiusFt * feetToMeters;
}

export function radiusMToFT(radiusM: number): number {
  const metersToFeet = 3.28084; // 1 meter = 3.28084 feet
  return radiusM * metersToFeet;
}

export const getDateLocalization = (locale: LangCode) => {
  const dateLocales = {
    ar,
    de,
    en: enUS,
    es,
    fr,
    pt,
  };
  return dateLocales[locale];
};

/**
 * Relative time for a timestamp known to be in the past ("3 minutes ago").
 * Clamped to now: a browser clock lagging the server would otherwise make a
 * server-generated timestamp look future-dated ("in less than a minute").
 */
export const formatTimeAgo = (date: string | number | Date, locale: LangCode) =>
  formatDistanceToNow(Math.min(new Date(date).getTime(), Date.now()), {
    locale: getDateLocalization(locale),
    addSuffix: true,
  });

export const formatLocalizedDate = ({
  date,
  formatString,
  locale,
}: {
  date: Date;
  formatString: string;
  locale: LangCode;
}) => {
  return format(date, formatString, { locale: getDateLocalization(locale) });
};

export function setFavicon(href: string, type = 'image/svg+xml') {
  if (typeof document === 'undefined') return;
  const ID = 'app-favicon';
  const finalHref = `${href}${href.includes('?') ? '&' : '?'}v=${Date.now()}`;

  let link = document.getElementById(ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = ID;
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = type;
  link.href = finalHref;
}

export const formatNumber = (
  value: string | number | null | undefined,
  options?: Intl.NumberFormatOptions
) => {
  if (value == null || value == undefined) return '';
  if (value == '') return '';
  if (value == '-') return '-';

  const formatter = new Intl.NumberFormat(
    'es-US',
    options ?? {
      maximumFractionDigits: 2,
    }
  );
  if (typeof value == 'string' && value.endsWith('.')) {
    return formatter.format(+value) + '.';
  }

  const converted = formatter.format(+value);
  if (converted == 'NaN') return '';
  return converted;
};

export const logMessage = (
  level: string,
  message: string,
  extra: Record<string, unknown> = {}
) => {
  if (level === 'error') {
    console.error(`Logger: [${level}] ${message}`, extra);
  } else {
    console.log(`Logger: [${level}] ${message}`, extra);
  }

  const attrs = { level, ...extra };
  import('@sentry/nextjs').then((Sentry) => {
    Sentry.withScope((scope) => {
      scope.setExtras(attrs);
      if (level === 'error') {
        Sentry.captureException(new Error(message), { extra: attrs });
        Sentry.logger.error(message, attrs);
      } else if (level === 'warning') {
        Sentry.logger.warn(message, attrs);
      } else {
        Sentry.logger.info(message, attrs);
      }
    });
  });
};

export function isMobileDevice() {
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    return /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  // Fallback: check window width (mobile typically < 768px)
  if (typeof window !== 'undefined' && window.innerWidth) {
    return window.innerWidth < 768;
  }

  return false;

}

export function detectMobilePlatform() {
  const userAgent = navigator?.userAgent || '';
  return /iPhone|iPad/i.test(userAgent)
    ? 'ios'
    : /Android/i.test(userAgent)
    ? 'android'
    : null;
}

/** Delay before redirecting to store if app did not open (visibility did not change) */
export const OPEN_APP_FALLBACK_DELAY_MS = 1500;

/**
 * Try to open the native app via deep link; if still on page after delay, open store.
 * Use for "Open in App" / "Return to ORTH App" buttons on mobile web.
 * No-op on desktop (platform not ios/android).
 */
export function openAppOrStore(): void {
  const platform = detectMobilePlatform();
  if (platform !== 'ios' && platform !== 'android') return;

  const deepLink = process.env.NEXT_PUBLIC_ORTH_DEEP_LINK;
  const storeUrl =
    platform === 'ios'
      ? process.env.NEXT_PUBLIC_IOS_APP_URL
      : process.env.NEXT_PUBLIC_ANDROID_APP_URL;

  if (!deepLink || !storeUrl) return;

  window.location.href = deepLink;

  const fallbackTimer = window.setTimeout(() => {
    window.location.href = storeUrl;
  }, OPEN_APP_FALLBACK_DELAY_MS);

  const clearFallback = () => {
    window.clearTimeout(fallbackTimer);
    document.removeEventListener('visibilitychange', clearFallback);
  };
  document.addEventListener('visibilitychange', clearFallback);
}

/**
 * Set a cookie on the client side
 */
export function setCookie(name: string, value: string, days: number = 365) {
  if (typeof document === 'undefined') return;
  
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  
  return null;
}

export function localizePath(path: string, target: LangCode, LANGS: Lang[]) {
  const parts = path.split('/');
  const known = new Set(LANGS.map((l) => l.code));
  if (known.has(parts[1] as LangCode)) {
    parts[1] = target;
    return parts.join('/') || '/';
  }
  return `/${target}${path.startsWith('/') ? '' : '/'}${path}`;
}

export const getCurrentTier = (currentPlanName?: string): TierName => {
  const name = (currentPlanName ?? '').toLowerCase();
  if (name === 'reverse_trial') return 'field';
  if (isEnterprisePlan(name)) return 'enterprise';
  if (name.includes('expert')) return 'fieldExpert';
  if (name.includes('pro')) return 'fieldPro';
  if (name.includes('plus')) return 'fieldPlus';
  if (name.includes('team')) return 'fieldTeams';
  return 'field';
};

export const getNextTier = (currentTier: TierName): TierName | null => {
  if (currentTier === 'fieldExpert') return null;
  // Enterprise is a contract, not a plan the app can upgrade — there is nothing
  // to sell it, so every caller that branches on an upgrade target falls back to
  // the "Book a call" variant (see `shared/plan.ts`).
  if (currentTier === 'enterprise') return null;
  if (currentTier === 'fieldPro') return 'fieldExpert';
  if (currentTier === 'fieldPlus') return 'fieldPro';
  return 'fieldPlus';
};
